
import React, {
    useEffect,
    useState,
} from 'react'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useCreateNewEvalDimensionMutation,
    useUpdateEvalDimensionMutation,
    useUpdateEvalDimensionStatusMutation,
} from '@/features/coachUserSessionEvals/evalDimensionsApiSlice'


/*
|--------------------------------------------------------------------------
| Evaluation Dimension Form
|--------------------------------------------------------------------------
|
| First Created - 29 Oct 2026
| Author - Atul Pandey
|
| Step 12B - Centralized Evaluation Framework
|
| Supports:
|   Create Dimension
|   Update Dimension
|   Activate / Deactivate Dimension
|
| Existing dimensions retain their category assignment
| until server-side historical usage checks are available.
|
|--------------------------------------------------------------------------
*/

const getId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(
        value._id || value.id || ''
    )

}


const EtEvalDimensionForm = ({
    dimension = null,
    categories = [],
    defaultCategoryId = '',
    onDone,
    onCancel,
}) => {

    const isEditing = Boolean(dimension?._id)


    /*
    |--------------------------------------------------------------------------
    | 01 - Form State
    |--------------------------------------------------------------------------
    */

    const [dimensionName, setDimensionName] =
        useState(dimension?.dimensionName || '')

    const [dimensionDesc, setDimensionDesc] =
        useState(dimension?.dimensionDesc || '')

    const [categoryId, setCategoryId] =
        useState(
            getId(dimension?.categoryId) ||
            defaultCategoryId
        )

    const [active, setActive] =
        useState(dimension?.active !== false)

    const [errorMessage, setErrorMessage] =
        useState('')


    /*
    |--------------------------------------------------------------------------
    | 02 - Synchronize Dimension
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        setDimensionName(
            dimension?.dimensionName || ''
        )

        setDimensionDesc(
            dimension?.dimensionDesc || ''
        )

        setCategoryId(
            getId(dimension?.categoryId) ||
            defaultCategoryId
        )

        setActive(
            dimension?.active !== false
        )

        setErrorMessage('')

    }, [
        dimension,
        defaultCategoryId,
    ])


    /*
    |--------------------------------------------------------------------------
    | 03 - Mutations
    |--------------------------------------------------------------------------
    */

    const [
        createDimension,
        {
            isLoading: isCreating,
        },
    ] = useCreateNewEvalDimensionMutation()

    const [
        updateDimension,
        {
            isLoading: isUpdating,
        },
    ] = useUpdateEvalDimensionMutation()

    const [
        updateDimensionStatus,
        {
            isLoading: isUpdatingStatus,
        },
    ] = useUpdateEvalDimensionStatusMutation()


    const isBusy =
        isCreating ||
        isUpdating ||
        isUpdatingStatus


    /*
    |--------------------------------------------------------------------------
    | 04 - Category Options
    |--------------------------------------------------------------------------
    |
    | Existing inactive categories remain visible
    | when editing a historical dimension.
    |
    */

    const categoryOptions = categories
        .filter(category =>
            category.active !== false ||
            getId(category) === categoryId
        )
        .slice()
        .sort(
            (a, b) =>
                (a.categoryName || '')
                    .localeCompare(
                        b.categoryName || ''
                    )
        )


    /*
    |--------------------------------------------------------------------------
    | 05 - Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async event => {

        event.preventDefault()

        setErrorMessage('')

        const name = dimensionName.trim()
        const description = dimensionDesc.trim()

        if (!name || !categoryId) {

            setErrorMessage(
                'Dimension name and category are required.'
            )

            return
        }

        try {

            if (isEditing) {

                await updateDimension({

                    id: dimension._id,

                    dimensionName: name,

                    dimensionDesc: description,

                    // Existing category remains unchanged.
                    categoryId: getId(
                        dimension.categoryId
                    ),

                }).unwrap()

                if (
                    active !==
                    (dimension.active !== false)
                ) {

                    await updateDimensionStatus({

                        id: dimension._id,

                        active,

                    }).unwrap()

                }

            } else {

                await createDimension({

                    categoryId,

                    dimensionName: name,

                    dimensionDesc: description,

                    active,

                }).unwrap()

            }

            onDone?.()

        } catch (error) {

            setErrorMessage(
                error?.data?.message ||
                error?.error ||
                'Unable to save evaluation dimension.'
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 06 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="rounded-lg border border-orange-200 bg-gray-50 p-3 font-poppins">

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">

                <div>

                    <p className="text-sm font-bold text-orange-600">
                        {isEditing
                            ? 'Update Evaluation Dimension'
                            : 'Create Evaluation Dimension'}
                    </p>

                    <p className="mt-1 text-[10px] italic text-gray-500">
                        Dimensions are the specific
                        capabilities scored by coaches.
                    </p>

                </div>

                {isBusy && (
                    <PulseLoader
                        color="#ea580c"
                        size={5}
                    />
                )}

            </div>


            <form
                onSubmit={handleSubmit}
                className="space-y-3"
            >

                {/* Category */}

                <div className="rounded bg-white p-3">

                    <label
                        htmlFor="eval-dimension-category"
                        className="mb-2 block text-xs font-bold text-gray-700"
                    >
                        Evaluation Category
                    </label>

                    <select
                        id="eval-dimension-category"
                        value={categoryId}
                        onChange={event =>
                            setCategoryId(
                                event.target.value
                            )
                        }
                        disabled={isBusy || isEditing}
                        required
                        className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs outline-none focus:border-orange-400 disabled:cursor-not-allowed"
                    >

                        <option value="">
                            Select Evaluation Category
                        </option>

                        {categoryOptions.map(category => (

                            <option
                                key={getId(category)}
                                value={getId(category)}
                            >
                                {category.categoryName}
                                {category.active === false
                                    ? ' (Inactive)'
                                    : ''}
                            </option>

                        ))}

                    </select>

                    {isEditing && (

                        <p className="mt-2 text-[10px] italic text-gray-500">
                            Category reassignment is locked
                            until historical evaluation usage
                            checks are implemented.
                        </p>

                    )}

                </div>


                {/* Dimension Name */}

                <div className="rounded bg-white p-3">

                    <label
                        htmlFor="eval-dimension-name"
                        className="mb-2 block text-xs font-bold text-gray-700"
                    >
                        Dimension Name
                    </label>

                    <input
                        id="eval-dimension-name"
                        type="text"
                        required
                        maxLength={150}
                        value={dimensionName}
                        onChange={event =>
                            setDimensionName(
                                event.target.value
                            )
                        }
                        disabled={isBusy}
                        placeholder="e.g. Speech Effectiveness"
                        className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs outline-none focus:border-orange-400"
                    />

                </div>


                {/* Description */}

                <div className="rounded bg-white p-3">

                    <label
                        htmlFor="eval-dimension-desc"
                        className="mb-2 block text-xs font-bold text-gray-700"
                    >
                        Description
                    </label>

                    <textarea
                        id="eval-dimension-desc"
                        rows={3}
                        value={dimensionDesc}
                        onChange={event =>
                            setDimensionDesc(
                                event.target.value
                            )
                        }
                        disabled={isBusy}
                        placeholder="Describe what the coach should assess..."
                        className="w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs outline-none focus:border-orange-400"
                    />

                </div>


                {/* Active Status */}

                <div className="rounded bg-white p-3">

                    <label className="flex cursor-pointer items-center gap-3">

                        <input
                            type="checkbox"
                            checked={active}
                            onChange={event =>
                                setActive(
                                    event.target.checked
                                )
                            }
                            disabled={isBusy}
                            className="accent-orange-600"
                        />

                        <div>

                            <p className="text-xs font-semibold text-gray-700">
                                Active Dimension
                            </p>

                            <p className="text-[10px] text-gray-500">
                                Deactivating prevents new
                                use while preserving history.
                            </p>

                        </div>

                    </label>

                </div>


                {errorMessage && (

                    <p
                        role="alert"
                        className="rounded bg-rose-50 p-2 text-[11px] text-rose-700"
                    >
                        {errorMessage}
                    </p>

                )}


                {/* Actions */}

                <div className="flex flex-wrap gap-2 rounded bg-white p-3">

                    <button
                        type="submit"
                        disabled={
                            isBusy ||
                            !dimensionName.trim() ||
                            !categoryId
                        }
                        className="rounded-full bg-lime-600 px-5 py-2 text-[11px] font-bold text-white hover:bg-lime-700 disabled:opacity-50"
                    >
                        {isBusy
                            ? 'Saving...'
                            : isEditing
                                ? 'Update Dimension'
                                : 'Create Dimension'}
                    </button>

                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isBusy}
                        className="rounded-full border border-orange-200 bg-white px-5 py-2 text-[11px] font-semibold text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>

                </div>

            </form>

        </div>

    )

}


export default EtEvalDimensionForm